import React, { useEffect, useMemo, useState } from 'react';
import {
  ResponsiveContainer, ScatterChart, Scatter, XAxis, YAxis, ZAxis, CartesianGrid, ReferenceArea,
} from 'recharts';
import ThemedChartTooltip, { ThemedTooltipBox } from '../../shared/ThemedChartTooltip';
import { useAimAioFunnel, useAimSnapshot } from '../lib/aimQueries';
import { isPending } from '../lib/queryState';
import {
  getAimOrganicRankings, getAimPositionVolatility,
} from '../../../api/auth';
import AimEmpty, { AimLoading, AimError } from '../primitives/AimEmpty';
import InsightCard from '../primitives/InsightCard';
import InfoTooltip from '../primitives/InfoTooltip';
import PeriodLabel from '../primitives/PeriodLabel';
import { buildOrganicMismatchInsight, buildAioExpansionInsight } from '../lib/insights';
import { sparklineStroke } from '../lib/sparklineStroke';
import { mismatchScatterPoints } from '../lib/mismatchScatter';
import { positionVolatilityRows, hasPositionSeries, positionVolatilityScopeLabel } from '../lib/positionVolatility';
import { buildPageRange } from '../lib/pagination';
import usePagedRows from '../lib/usePagedRows';
import AimPagination from '../primitives/AimPagination';
import { TABLE_FRAME, TABLE_FRAME_FLUSH, TABLE_THEAD, TABLE_ROW } from '../../shared/tableStyles';

// Rank pill colors map to the spec v2 Rating Color Scale (lines 174-182:
// Excellent / Good / Average / Below Average / Poor) so rank pills stay
// uniform with RatingColorBar and other 0-100 visualizations.
function rankColor(pos) {
  if (pos == null) return '#9CA3AF';   // gray fallback
  if (pos === 1) return '#10B981';     // Emerald — Excellent
  if (pos === 2) return '#22C55E';     // Green — Good
  if (pos === 3) return '#EAB308';     // Yellow — Average
  if (pos <= 10) return '#F97316';     // Orange — Below Average
  return '#EF4444';                     // Red — Poor
}

function OrganicRankings({ clientId, runId }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    if (!clientId || !runId) { setRows([]); setLoading(false); return; }
    setLoading(true);
    getAimOrganicRankings(clientId, { runId, limit: 100 })
      .then((resp) => { if (!cancelled) setRows(resp?.data || []); })
      .catch(() => { if (!cancelled) setRows([]); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId, runId]);

  // Rank ascending (1 first); unranked (client_rank null) sorts to the bottom.
  const sortedRows = useMemo(
    () => [...rows].sort((a, b) => {
      const ar = a.client_rank == null ? Infinity : Number(a.client_rank);
      const br = b.client_rank == null ? Infinity : Number(b.client_rank);
      return ar - br;
    }),
    [rows],
  );
  const { visibleRows, page, setPage, pageCount, totalItems } = usePagedRows(sortedRows, 25);

  if (loading) return <AimLoading height={160} />;
  if (rows.length === 0) return <AimEmpty message="No organic ranking data captured for this run." />;
  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Organic Rankings (7A)</h3>
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5">
          <strong className="font-semibold">Total Organic Results</strong> is the number of organic (non-paid) search results Google returned for that query — read it alongside your rank (e.g., 5 of 12 is much stronger than 5 of 200).
        </p>
      </div>
      <div className={`${TABLE_FRAME_FLUSH} responsive-card-table-wrapper`}>
      <table className="w-full text-sm text-gray-900 dark:text-gray-100 min-w-[600px] responsive-card-table">
        <thead className={TABLE_THEAD}>
          <tr>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">Query</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Your Rank</th>
            <th className="text-right px-4 py-2.5 whitespace-nowrap">Total Organic Results</th>
            <th className="text-left px-4 py-2.5 whitespace-nowrap">#1 Domain</th>
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r, i) => (
            <tr key={i} className={TABLE_ROW}>
              <td className="px-4 py-3 truncate max-w-[320px]" title={r.query} data-label="Query">{(r.query || '').slice(0, 50)}</td>
              <td className="px-4 py-3 text-right" data-label="Your Rank">
                {r.client_rank == null ? (
                  <span
                    className="inline-flex items-center px-2 py-0.5 rounded border border-red-300 dark:border-red-800/60 bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-xs font-medium"
                    title="Your property did not appear in this query's organic results"
                  >
                    Not ranked
                  </span>
                ) : (
                  <span
                    className="inline-flex items-center justify-center min-w-[32px] px-2 py-0.5 rounded text-white text-xs font-semibold"
                    style={{ backgroundColor: rankColor(Number(r.client_rank)) }}
                  >
                    {r.client_rank}
                  </span>
                )}
              </td>
              <td className="px-4 py-3 text-right tabular-nums text-xs" data-label="Total Organic Results">{r.total_results}</td>
              <td className="px-4 py-3 text-xs text-gray-600 dark:text-gray-400 truncate max-w-[260px]" title={r.rank_1_domain} data-label="#1 Domain">
                {r.rank_1_domain || '—'}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="px-4 pb-3">
        <AimPagination page={page} pageCount={pageCount} onPage={setPage} totalItems={totalItems} label="query" />
      </div>
    </div>
  );
}

function AioPenetration({ clientId, runId }) {
  const { data: funnel, loading } = useAimAioFunnel(clientId, runId);
  const { data: p9 } = useAimSnapshot(clientId, 'P9', '', runId);
  if (loading) return <AimLoading height={140} />;
  if (!funnel) return <AimEmpty message="No AI Overviews funnel data for this run." />;

  const insight = buildAioExpansionInsight(Number(funnel.trigger_pct || 0));

  const stages = [
    { label: 'Total queries', value: Number(funnel.total_queries || 0), color: '#9CA3AF' },
    { label: `AI Overviews triggered (${Number(funnel.trigger_pct || 0).toFixed(1)}%)`, value: Number(funnel.aio_triggered || 0), color: '#3B82F6' },
    { label: `You captured (${Number(funnel.capture_pct || 0).toFixed(1)}%)`, value: Number(funnel.client_captured || 0), color: '#10B981' },
  ];
  const max = Math.max(...stages.map((s) => s.value), 1);

  const p9Value = p9?.value_numeric != null ? Number(p9.value_numeric) : null;

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <div className="flex items-baseline justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {'AI Overviews Penetration ('}
              <InfoTooltip
                label="P9 — AI Overviews Penetration"
                trigger={<span className="border-b border-dotted border-gray-400 dark:border-gray-500 cursor-help">P9</span>}
              >
                <strong>P9 — AI Overviews Penetration.</strong> Google AI Overviews coverage. The % of your queries that trigger an
                AI Overview, and — when one is shown — the % of the time your
                property appears in it. As Google expands AI Overviews to more travel
                queries, you want to already be optimized.
              </InfoTooltip>
              {')'}
            </h3>
          </div>
          <div className="text-xs text-gray-500 dark:text-gray-400 text-right">
            <div>
              AI Overviews visibility (this run):{' '}
              <span className="font-semibold text-gray-900 dark:text-white">
                {Number(funnel.overall_pct || 0).toFixed(1)}%
              </span>
            </div>
            {p9Value != null && (
              <div className="text-[11px]">
                30-day average:{' '}
                <span className="font-semibold text-gray-900 dark:text-white">
                  {p9Value.toFixed(1)}%
                </span>
              </div>
            )}
          </div>
        </div>
        <div className="space-y-2">
          {stages.map((s) => (
            <div key={s.label}>
              <div className="flex items-center justify-between text-xs text-gray-600 dark:text-gray-400">
                <span>{s.label}</span>
                <span className="tabular-nums">{s.value.toLocaleString()}</span>
              </div>
              <div className="h-3 rounded bg-gray-100 dark:bg-gray-800 overflow-hidden">
                <div className="h-full" style={{ width: `${(s.value / max) * 100}%`, backgroundColor: s.color }} />
              </div>
            </div>
          ))}
        </div>
      </div>
      {insight && <InsightCard {...insight} />}
    </div>
  );
}

// Pair each scatter point's Organic rank + AI mention together in the tooltip
// rather than letting Recharts list all X-axis values, then all Y-axis values.
function MismatchTooltipContent({ active, payload }) {
  if (!active || !Array.isArray(payload) || payload.length === 0) return null;
  const seen = new Map();
  for (const entry of payload) {
    const p = entry?.payload;
    if (!p) continue;
    const key = `${p.query || ''}|${p.organic}|${p.ai}`;
    if (!seen.has(key)) seen.set(key, p);
  }
  const points = Array.from(seen.values()).sort((a, b) => {
    const ar = Number(a.organic);
    const br = Number(b.organic);
    if (Number.isFinite(ar) && Number.isFinite(br) && ar !== br) return ar - br;
    return (Number(b.ai) || 0) - (Number(a.ai) || 0);
  });
  return (
    <ThemedTooltipBox>
      <div className="space-y-2">
        {points.map((p, i) => (
          <div key={i} className="space-y-0.5">
            <div>Organic rank: {p.organic}</div>
            <div>AI mention: {typeof p.ai === 'number' && Number.isFinite(p.ai) ? `${p.ai.toFixed(1)}%` : '—'}</div>
          </div>
        ))}
      </div>
    </ThemedTooltipBox>
  );
}

function OrganicAiMismatch({ clientId, runId }) {
  // P16 snapshot rows: { query, organic_position, ai_answers, providers_answering, providers_mentioning,
  // ai_mention_pct, ai_mention_count, mismatch_score }.
  const snapQuery = useAimSnapshot(clientId, 'P16', '', runId);
  const { data: snap, error, refresh } = snapQuery;
  const rows = useMemo(() => (Array.isArray(snap?.value_breakdown) ? snap.value_breakdown : []), [snap]);
  const insight = buildOrganicMismatchInsight(rows);

  const scatter = useMemo(() => mismatchScatterPoints(rows), [rows]);

  if (isPending(snapQuery)) return <AimLoading height={220} />;
  // This card has no second source, so a failed fetch is unambiguously an error
  // rather than an empty run.
  if (error) return <AimError message="Organic-AI mismatch data unavailable." onRetry={refresh} />;
  if (rows.length === 0) return <AimEmpty message="No organic-AI mismatch data for this run." />;

  const xMax = Math.max(10, ...scatter.map((r) => Number(r.organic) || 0));

  const colorByType = {
    organic_strong_ai_weak: '#F97316',
    ai_strong_organic_weak: '#3B82F6',
    aligned_strong: '#10B981',
    moderate_or_weak: '#EF4444',
  };

  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
        <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Organic vs AI Mismatch (7C)</h3>
        <PeriodLabel snapshot={snap} />
        <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
          Queries where your Google rank doesn&apos;t line up with AI&apos;s mention behavior. &ldquo;Organic strong / AI weak&rdquo; is the highest-leverage place to optimize — Google knows you, but AI doesn&apos;t yet.
        </p>
        <div className="flex justify-between px-3 mb-[15px] text-[10px] font-medium">
          <span className="inline-flex items-center gap-1 text-emerald-700 dark:text-emerald-400">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colorByType.aligned_strong }} aria-hidden="true" />
            Aligned Strong ✅
          </span>
          <span className="inline-flex items-center gap-1 text-blue-700 dark:text-blue-400">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colorByType.ai_strong_organic_weak }} aria-hidden="true" />
            AI Strong / Organic Weak
          </span>
        </div>
        <ResponsiveContainer width="100%" height={300}>
            <ScatterChart margin={{ top: 5, right: 16, left: 0, bottom: 5 }}>
              <CartesianGrid stroke="rgba(148,163,184,0.2)" />
              <XAxis dataKey="organic" name="Organic rank" type="number" domain={[0, 'auto']} allowDecimals={false} tickCount={6} tick={{ fontSize: 11 }} label={{ value: '← Better rank', position: 'bottom', fontSize: 10 }} />
              <YAxis dataKey="ai" name="AI mention" domain={[0, 100]} tick={{ fontSize: 11 }} />
              <ZAxis range={[60, 160]} />
              {/* Quadrant background shading — light tints in series colors */}
              <ReferenceArea x1={5} x2={xMax} y1={50} y2={100} fill={colorByType.ai_strong_organic_weak} fillOpacity={0.08} stroke="none" />
              <ReferenceArea x1={0} x2={5} y1={50} y2={100} fill={colorByType.aligned_strong} fillOpacity={0.08} stroke="none" />
              <ReferenceArea x1={5} x2={xMax} y1={0} y2={50} fill={colorByType.moderate_or_weak} fillOpacity={0.08} stroke="none" />
              <ReferenceArea x1={0} x2={5} y1={0} y2={50} fill={colorByType.organic_strong_ai_weak} fillOpacity={0.08} stroke="none" />
              <ThemedChartTooltip content={<MismatchTooltipContent />} />
              {Object.keys(colorByType).map((type) => (
                <Scatter key={type} data={scatter.filter((r) => r.type === type)} fill={colorByType[type]} />
              ))}
              <Scatter data={scatter.filter((r) => !colorByType[r.type])} fill="#6366F1" />
            </ScatterChart>
          </ResponsiveContainer>
        <div className="flex justify-between px-3 mt-1 text-[10px] font-medium">
          <span className="inline-flex items-center gap-1 text-orange-700 dark:text-orange-400">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colorByType.organic_strong_ai_weak }} aria-hidden="true" />
            Organic Strong / AI Weak ⚠
          </span>
          <span className="inline-flex items-center gap-1 text-red-700 dark:text-red-400">
            <span className="inline-block w-1.5 h-1.5 rounded-full" style={{ backgroundColor: colorByType.moderate_or_weak }} aria-hidden="true" />
            Aligned Weak
          </span>
        </div>
      </div>
      {insight && <InsightCard {...insight} />}
    </div>
  );
}

function Sparkline({ positions, volatility }) {
  if (!Array.isArray(positions) || positions.length < 2) return null;
  const w = 80, h = 24;
  const max = Math.max(...positions);
  const min = Math.min(...positions);
  const span = Math.max(1, max - min);
  const points = positions.map((p, i) => {
    const x = (i / (positions.length - 1)) * w;
    const y = h - ((p - min) / span) * h;
    return `${x},${y}`;
  }).join(' ');
  return (
    <svg width={w} height={h} className="inline-block">
      <polyline fill="none" stroke={sparklineStroke(volatility)} strokeWidth="1.5" points={points} />
    </svg>
  );
}

const POSITION_VOLATILITY_PAGE_SIZE = 25;

function PositionVolatility({ clientId }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  useEffect(() => {
    let cancelled = false;
    if (!clientId) { setInfo(null); setLoading(false); return; }
    setLoading(true);
    setPage(0);
    getAimPositionVolatility(clientId)
      .then((resp) => { if (!cancelled) setInfo(resp?.data || null); })
      .catch(() => { if (!cancelled) setInfo(null); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [clientId]);

  if (loading) return <AimLoading height={140} />;
  if (info?.status === 'insufficient_data') {
    return <AimEmpty message={`Position volatility requires 3+ completed runs. You have ${info.runs_available}. Check back in ${info.weeks_needed} weeks.`} />;
  }
  const rows = positionVolatilityRows(info);
  if (rows.length === 0) return <AimEmpty message="No volatility data available." />;

  function volatilityColor(v) {
    if (v == null) return 'text-gray-500';
    if (v < 1.0) return 'text-emerald-600 dark:text-emerald-400';
    if (v < 2.5) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  }

  const pageCount = Math.max(1, Math.ceil(rows.length / POSITION_VOLATILITY_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const start = safePage * POSITION_VOLATILITY_PAGE_SIZE;
  const visibleRows = rows.slice(start, start + POSITION_VOLATILITY_PAGE_SIZE);
  const hasTrend = hasPositionSeries(rows);
  const scopeLabel = positionVolatilityScopeLabel(info);

  return (
    <div className="rounded-xl border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4">
      <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Position Volatility (7D)</h3>
      <p className="text-[11px] text-gray-500 dark:text-gray-400 mt-0.5 mb-3">
        How much your Google rank shifts week-over-week. High volatility (red) means unstable rankings worth investigating; low (green) means you&apos;re holding steady.
      </p>
      {scopeLabel && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400 -mt-2 mb-3">
          {scopeLabel}
        </p>
      )}
      {!hasTrend && (
        <p className="text-[11px] text-gray-500 dark:text-gray-400 -mt-2 mb-3">
          The per-run trend sparkline isn&apos;t available from this endpoint, so that column is
          hidden. Best / worst and volatility above are unaffected.
        </p>
      )}
      <div className={`${TABLE_FRAME} responsive-card-table-wrapper`}>
      <table className="w-full text-sm text-gray-900 dark:text-gray-100 min-w-[640px] responsive-card-table">
        <thead className={TABLE_THEAD}>
          <tr>
            <th className="text-left px-3 py-2.5 whitespace-nowrap">Query × Provider</th>
            <th className="text-right px-3 py-2.5 whitespace-nowrap">Avg Position</th>
            <th className="text-right px-3 py-2.5 whitespace-nowrap">Best / Worst</th>
            <th className="text-right px-3 py-2.5 whitespace-nowrap">Volatility</th>
            {hasTrend && <th className="text-right px-3 py-2.5 whitespace-nowrap">Trend</th>}
          </tr>
        </thead>
        <tbody>
          {visibleRows.map((r, i) => (
            <tr key={`${start}-${i}`} className={TABLE_ROW}>
              <td className="py-2" data-label="Query × Provider">
                <div className="truncate max-w-[320px] font-medium" title={r.query}>{r.query}</div>
                <div className="text-xs text-gray-500 dark:text-gray-400">{r.provider}</div>
              </td>
              <td className="py-2 text-right tabular-nums" data-label="Avg Position">{r.avg_position != null ? Number(r.avg_position).toFixed(1) : '—'}</td>
              <td className="py-2 text-right tabular-nums text-xs" data-label="Best / Worst">{r.best ?? '—'} / {r.worst ?? '—'}</td>
              <td className={`py-2 text-right tabular-nums font-semibold ${volatilityColor(r.volatility)}`} data-label="Volatility">{r.volatility != null ? Number(r.volatility).toFixed(2) : '—'}</td>
              {hasTrend && (
                <td className="py-2 text-right" data-label="Trend">
                  <Sparkline positions={r.positions} volatility={r.volatility} />
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      </div>
      <div className="mt-3 flex items-center justify-between gap-2 text-xs text-gray-600 dark:text-gray-400 max-[499px]:flex-col max-[499px]:items-stretch max-[499px]:gap-3">
        <span>{rows.length} row{rows.length === 1 ? '' : 's'}</span>
        <nav aria-label="Position Volatility pagination" className="flex items-center gap-1 max-[499px]:w-full max-[499px]:justify-between">
          <button
            type="button"
            onClick={() => setPage((p) => Math.max(0, p - 1))}
            disabled={safePage === 0}
            className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800/60 max-[499px]:px-3"
          >
            Previous
          </button>
          {buildPageRange(safePage, pageCount).map((p, i) => (
            p === '…' ? (
              <span key={`ellipsis-${i}`} className="px-1 text-gray-400 max-[499px]:hidden" aria-hidden="true">…</span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => setPage(p)}
                aria-current={p === safePage ? 'page' : undefined}
                className={`min-w-[28px] px-2 py-1 rounded border text-xs max-[499px]:hidden ${
                  p === safePage
                    ? 'border-[#32B1FF] bg-[#32B1FF] text-white'
                    : 'border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800/60'
                }`}
              >
                {p + 1}
              </button>
            )
          ))}
          <button
            type="button"
            onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))}
            disabled={safePage >= pageCount - 1}
            className="px-2 py-1 rounded border border-gray-200 dark:border-gray-700 disabled:opacity-40 disabled:cursor-not-allowed hover:bg-gray-50 dark:hover:bg-gray-800/60 max-[499px]:px-3"
          >
            Next
          </button>
        </nav>
      </div>
    </div>
  );
}

export default function GoogleOrganicTab({ clientId, runId }) {
  return (
    <div className="space-y-5">
      <OrganicRankings clientId={clientId} runId={runId} />
      <AioPenetration clientId={clientId} runId={runId} />
      <OrganicAiMismatch clientId={clientId} runId={runId} />
      <PositionVolatility clientId={clientId} />
    </div>
  );
}
